import type { InferOutput } from 'valibot'

import type { FeatureFlag } from '../libs/feature-flags'

import { errorMessageFrom } from '@moeru/std'
import { isStageTamagotchi } from '@proj-airi/stage-shared'
import { useLocalStorageManualReset } from '@proj-airi/stage-shared/composables'
import { defineStore } from 'pinia'
import { parse } from 'valibot'
import { computed, onScopeDispose, shallowRef, watch } from 'vue'

import { useCloudFetch } from '../composables/cloud'
import { featureFlagPreferencesSchema, featureFlagResponseSchema, featureFlagVariantPreferencesSchema, offeredFeatureFlags, resolveFeatureFlag, resolveFeatureVariant } from '../libs/feature-flags'
import { useAuthStore } from './auth'

/** Owns device preferences and a non-persisted policy snapshot for the current account. */
export const useFeatureFlagsStore = defineStore('feature-flags', () => {
  const auth = useAuthStore()
  const cloudFetch = useCloudFetch()
  const offered = offeredFeatureFlags(isStageTamagotchi())
  const preferences = useLocalStorageManualReset<Record<string, boolean>>('settings/feature-flags', {}, {
    serializer: {
      read: value => parse(featureFlagPreferencesSchema, JSON.parse(value)),
      write: value => JSON.stringify(value),
    },
  })
  /** The variant that the user chose for each feature that has variants. It is a device choice. */
  const variantPreferences = useLocalStorageManualReset<Record<string, string>>('settings/feature-flag-variants', {}, {
    serializer: {
      read: value => parse(featureFlagVariantPreferencesSchema, JSON.parse(value)),
      write: value => JSON.stringify(value),
    },
  })
  const policies = shallowRef<InferOutput<typeof featureFlagResponseSchema>['flags']>([])
  const loading = shallowRef(false)
  const error = shallowRef<string>()
  let requestVersion = 0
  let request: AbortController | undefined

  function preferenceKey(feature: FeatureFlag) {
    return JSON.stringify([feature.key, feature.availability === 'cloud' ? auth.user?.id : null])
  }

  function decision(feature: FeatureFlag) {
    return resolveFeatureFlag(feature, preferences.value[preferenceKey(feature)], policies.value.find(policy => policy.key === feature.key), Boolean(auth.user?.id && auth.token))
  }

  /** The variant of a feature: the saved one while the feature still offers it, otherwise the first one. */
  function variantOf(key: string): string | undefined {
    return resolveFeatureVariant(offered.find(feature => feature.key === key), variantPreferences.value[key])
  }

  /** Saves the variant of a feature. A value that the feature does not offer changes nothing. */
  function setVariant(key: string, value: string | undefined) {
    const options = offered.find(feature => feature.key === key)?.variants?.options
    if (!value || !options?.some(option => option.value === value) || variantPreferences.value[key] === value)
      return
    variantPreferences.value = { ...variantPreferences.value, [key]: value }
  }

  const features = computed(() => offered.map(feature => ({
    ...feature,
    ...decision(feature),
    preference: preferences.value[preferenceKey(feature)],
    variant: variantOf(feature.key),
  })).filter(feature => feature.selectable))

  function setPreference(key: string, value: boolean | undefined) {
    const feature = offered.find(feature => feature.key === key)
    if (!feature || !decision(feature).selectable)
      return
    const storageKey = preferenceKey(feature)
    if (preferences.value[storageKey] === value)
      return

    const next = { ...preferences.value }
    if (value === undefined)
      delete next[storageKey]
    else
      next[storageKey] = value
    preferences.value = next
  }

  /** Unknown features, and features that this app does not offer, stay disabled. */
  function isEnabled(key: string): boolean {
    const feature = offered.find(feature => feature.key === key)
    if (!feature)
      return false

    return decision(feature).enabled
  }

  async function refresh() {
    // A private resource API has no official Cloud policies or Cloud token issuer.
    if (import.meta.env.VITE_SELF_HOSTED === 'true') {
      policies.value = []
      loading.value = false
      error.value = undefined
      return
    }
    const version = ++requestVersion
    request?.abort()
    request = new AbortController()
    policies.value = []
    loading.value = true
    error.value = undefined
    try {
      const response = await cloudFetch('/v1/feature-flags', {
        signal: AbortSignal.any([request.signal, AbortSignal.timeout(10_000)]),
      })
      if (!response.ok)
        throw new Error(`Feature policy request failed (${response.status})`)
      const result = parse(featureFlagResponseSchema, await response.json())
      if (version === requestVersion)
        policies.value = result.flags
    }
    catch (cause) {
      if (version === requestVersion)
        error.value = errorMessageFrom(cause) ?? 'Feature policy request failed'
    }
    finally {
      if (version === requestVersion)
        loading.value = false
    }
  }

  watch([() => auth.sessionVersion, () => auth.user?.id, () => auth.token], () => {
    requestVersion++
    request?.abort()
    policies.value = []
    error.value = undefined
  }, { flush: 'sync' })

  watch([() => auth.sessionVersion, () => auth.user?.id, () => auth.token], () => {
    void refresh()
  }, { immediate: true })

  onScopeDispose(() => {
    requestVersion++
    request?.abort()
  })

  return { features, loading, error, setPreference, isEnabled, variantOf, setVariant, refresh }
})
